import React, { useEffect, useState } from 'react';
import { Task, TaskCreateInput, TaskPriority, TaskTemplate, TaskType } from '../types/task';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';

interface CreateTaskModalProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  taskOrder: number;
  onSuccess: (task: Task) => void;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface JobTask {
  id: string;
  task_number: string;
  task_type: TaskType;
  status: string;
}

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  open,
  onClose,
  jobId,
  taskOrder,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<Partial<TaskCreateInput>>({
    task_type: TaskType.DELIVERY,
    task_order: taskOrder,
    job_id: jobId,
    priority: TaskPriority.NORMAL,
    assigned_to_user_id: '',
    instructions: '',
    requires_photo: false,
    task_template_id: undefined,
    depends_on_task_ids: [],
  });
  const [users, setUsers] = useState<User[]>([]);
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [jobTasks, setJobTasks] = useState<JobTask[]>([]);
  const [suggestedDeps, setSuggestedDeps] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      loadUsers();
      loadTemplates();
      loadJobTasks();
      setFormData(prev => ({ ...prev, task_order: taskOrder, job_id: jobId }));
    }
  }, [open, taskOrder, jobId]);

  // Load suggested dependencies when task type changes
  useEffect(() => {
    if (formData.task_type) {
      loadSuggestedDependencies(formData.task_type);
    }
  }, [formData.task_type, jobId]);

  // Auto-select first template when templates load or task type changes
  useEffect(() => {
    if (open && formData.task_type && templates.length > 0) {
      loadTemplatesForType(formData.task_type);
    }
  }, [formData.task_type, templates, open]);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users?is_active=true&limit=10', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.users) {
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/tasks/templates', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setTemplates(data.data || []);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const loadJobTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/job/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setJobTasks(data.data || []);
      }
    } catch (err) {
      console.error('Error loading job tasks:', err);
    }
  };

  const loadSuggestedDependencies = async (taskType: TaskType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/dependencies/suggest?jobId=${jobId}&taskType=${taskType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        const suggested = data.data?.suggested_task_ids || [];
        setSuggestedDeps(suggested);
        setFormData(prev => ({ ...prev, depends_on_task_ids: suggested }));
      }
    } catch (err) {
      console.error('Error loading suggested dependencies:', err);
    }
  };

  const handleDependencyToggle = (taskId: string, checked: boolean) => {
    setFormData(prev => {
      const current = prev.depends_on_task_ids || [];
      const updated = checked
        ? [...current, taskId]
        : current.filter(id => id !== taskId);
      return { ...prev, depends_on_task_ids: updated };
    });
  };

  const formatTaskType = (type: string) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const loadTemplatesForType = (taskType: TaskType) => {
    // Auto-select first available template for task type
    const firstTemplate = templates.find(t => t.task_type === taskType && t.is_active);
    if (firstTemplate) {
      setSelectedTemplate(firstTemplate);
      setFormData(prev => ({
        ...prev,
        task_template_id: firstTemplate.id,
        instructions: firstTemplate.default_instructions || '',
        priority: firstTemplate.default_priority,
        requires_photo: firstTemplate.requires_photo,
      }));
    } else {
      setSelectedTemplate(null);
      setFormData(prev => ({ ...prev, task_template_id: undefined }));
    }
  };

  const handleTemplateChange = (templateId: string) => {
    if (templateId === 'none') {
      setSelectedTemplate(null);
      setFormData(prev => ({
        ...prev,
        task_template_id: undefined,
        instructions: '',
        priority: TaskPriority.NORMAL,
        requires_photo: false,
      }));
      return;
    }

    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setFormData(prev => ({
        ...prev,
        task_template_id: template.id,
        instructions: template.default_instructions || '',
        priority: template.default_priority,
        requires_photo: template.requires_photo,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const response = await fetch('/api/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          assigned_to_user_id: formData.assigned_to_user_id || null,
          created_by_user_id: user.id,
        }),
      });

      const data = await response.json();

      if (data.success) {
        onSuccess(data.data);
        onClose();
        setFormData({
          task_type: TaskType.DELIVERY,
          task_order: taskOrder,
          job_id: jobId,
          priority: TaskPriority.NORMAL,
          assigned_to_user_id: '',
          instructions: '',
          requires_photo: false,
          task_template_id: undefined,
          depends_on_task_ids: [],
        });
        setSelectedTemplate(null);
        setSuggestedDeps([]);
      } else {
        setError(data.message || 'Failed to create task');
      }
    } catch (err) {
      setError('An error occurred while creating the task');
      console.error('Error creating task:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const availableTemplates = templates.filter(t => t.task_type === formData.task_type && t.is_active);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Task</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>
          )}

          <div>
            <Label htmlFor="task_type">Task Type *</Label>
            <Select
              value={formData.task_type}
              onValueChange={(value) =>
                setFormData({ ...formData, task_type: value as TaskType })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(TaskType).map((type) => (
                  <SelectItem key={type} value={type}>
                    {formatTaskType(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Template Selector */}
          {availableTemplates.length > 0 && (
            <div>
              <Label htmlFor="template">Use Template (Optional)</Label>
              <Select
                value={selectedTemplate?.id || 'none'}
                onValueChange={handleTemplateChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No template - create from scratch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template - create from scratch</SelectItem>
                  {availableTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                      {template.template_items && template.template_items.length > 0 && (
                        <span className="text-xs text-muted-foreground ml-2">
                          ({template.template_items.length} checklist items)
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTemplate && (
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedTemplate.description || 'Template will auto-populate instructions and checklist items'}
                </p>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="priority">Priority</Label>
            <Select
              value={formData.priority}
              onValueChange={(value) =>
                setFormData({ ...formData, priority: value as TaskPriority })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TaskPriority.LOW}>Low</SelectItem>
                <SelectItem value={TaskPriority.NORMAL}>Normal</SelectItem>
                <SelectItem value={TaskPriority.HIGH}>High</SelectItem>
                <SelectItem value={TaskPriority.URGENT}>Urgent</SelectItem>
              </SelectContent>
            </Select>
            {selectedTemplate && (
              <p className="text-xs text-muted-foreground mt-1">
                From template (you can change it)
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="assigned_to_user_id">Assign To</Label>
            <Select
              value={formData.assigned_to_user_id || 'unassigned'}
              onValueChange={(value) =>
                setFormData({ ...formData, assigned_to_user_id: value === 'unassigned' ? '' : value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="instructions">Instructions</Label>
            <Textarea
              id="instructions"
              value={formData.instructions || ''}
              onChange={(e) =>
                setFormData({ ...formData, instructions: e.target.value })
              }
              rows={4}
              placeholder="Task instructions..."
            />
            {selectedTemplate && formData.instructions && (
              <p className="text-xs text-muted-foreground mt-1">
                From template (you can modify it)
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="requires_photo"
              checked={formData.requires_photo}
              onChange={(e) =>
                setFormData({ ...formData, requires_photo: e.target.checked })
              }
              className="h-4 w-4"
            />
            <Label htmlFor="requires_photo" className="cursor-pointer">
              Requires photo documentation
            </Label>
            {selectedTemplate && (
              <span className="text-xs text-muted-foreground">(from template)</span>
            )}
          </div>

          {/* Dependencies Section */}
          {jobTasks.length > 0 && (
            <div>
              <Label>Dependencies (must complete first)</Label>
              <div className="mt-2 max-h-32 overflow-y-auto border rounded p-2 space-y-2">
                {jobTasks.map((jt) => {
                  const isSelected = formData.depends_on_task_ids?.includes(jt.id) || false;
                  const isSuggested = suggestedDeps.includes(jt.id);
                  return (
                    <div key={jt.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`dep-${jt.id}`}
                        checked={isSelected}
                        onCheckedChange={(checked) => handleDependencyToggle(jt.id, !!checked)}
                      />
                      <Label htmlFor={`dep-${jt.id}`} className="cursor-pointer text-sm flex-1">
                        {jt.task_number} - {formatTaskType(jt.task_type)}
                        <span className={`ml-1 text-xs ${jt.status === 'completed' ? 'text-green-600' : 'text-muted-foreground'}`}>
                          ({jt.status})
                        </span>
                      </Label>
                      {isSuggested && (
                        <span className="text-xs text-blue-600">suggested</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {suggestedDeps.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Auto-suggested based on task type workflow
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Task'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTaskModal;
